import React, {useEffect, useMemo, useRef, useState} from "react";
import {BulkChangePriceAffectedProductsDataGridCellKeys} from "../../../../core/constants/table/data-grid-column-keys";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../packages/data-grid";
import Button from "../../base/button/button";
import SearchInput from "../../base/input/search_input";
import PartnerDataGrid from "../../../containers/data-grid";
import {deepEqual} from "../../../../core/services/utils/utils";
import {DefaultInitialPaginationInfo, DefaultPaginationInfo} from "../../../../core/constants/enums";
import ProductInformationPopover from "../draggable-popover/product-information-popover";

const orderByPropNames = [
    BulkChangePriceAffectedProductsDataGridCellKeys.partNumber,
    BulkChangePriceAffectedProductsDataGridCellKeys.mainCategory,
    BulkChangePriceAffectedProductsDataGridCellKeys.index,
];

const BulkPriceChangeAffectedProductsTable = ({
                                                  requestId,
                                                  selectedItemIds,
                                                  disabled,
                                                  toggleExcludedProduct,
                                                  hideTitle = false,
                                                  saveConfig,
                                              }) => {
    const [products, setProducts] = useState([]);
    const [keyword, setKeyword] = useState('');
    const [loading, setLoading] = useState(true);
    const isMounted = useIsMounted();
    const prevSelectedItemIds = useRef(selectedItemIds);

    const includeAction = useMemo(() => !!toggleExcludedProduct, [toggleExcludedProduct])

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "ID",
                name: BulkChangePriceAffectedProductsDataGridCellKeys.index,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.string,
                width: 0,
            },
            {
                title: "Part Number",
                name: BulkChangePriceAffectedProductsDataGridCellKeys.partNumber,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Category",
                name: BulkChangePriceAffectedProductsDataGridCellKeys.mainCategory,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                name: BulkChangePriceAffectedProductsDataGridCellKeys.remove,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 200,
            },
        ].filter(e => includeAction ? true : e.name !== BulkChangePriceAffectedProductsDataGridCellKeys.remove), [includeAction])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: saveConfig?.name,
        version: saveConfig?.version,
        sortByObjectMap: {
            property: 'field',
            descending: 'descending',
        },
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize]);
    const defaultInitialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultInitialPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultInitialPaginationInfo, [dataGridPageSize])

    const [paginationInfo, setPaginationInfo] = useState(defaultInitialPaginationInfo);
    const [orderBy, setOrderBy] = useState(dataGridSortBy);

    /**
     * With each change in the [selectedParams] or [requestId]:
     * - resets the [keywords] and [products] state values.
     */
    useEffect(() => {
        if (deepEqual(prevSelectedItemIds.current, selectedItemIds))
            return;
        prevSelectedItemIds.current = selectedItemIds;
        setProducts([]);
        setKeyword(null);
    }, [selectedItemIds])

    /**
     * With each change in the [selectedParams] or [requestId] or [keyword] or [orderBy]:
     * - resets the [pagination] state value to trigger the search function.
     */
    useEffect(() => {
        if (keyword === null)
            return setKeyword('');
        setPaginationInfo(defaultInitialPaginationInfo)
    }, [selectedItemIds, keyword, orderBy, requestId])

    /**
     * With each change in the [pagination] state value:
     * - searches for the affected products of the current bulk-price-change request.
     */
    useEffect(() => {
        if (selectedItemIds && !selectedItemIds.length)
            return;
        if (paginationInfo.currentPage === defaultInitialPaginationInfo.currentPage) {
            return setPaginationInfo(initialPaginationInfo)
        }
        searchAffectedProducts().then();
    }, [paginationInfo.currentPage, paginationInfo.pageSize])

    /**
     * Searches among the affected products of the current bulk-price-change request with the given constraints:
     * @return {Promise<void>}
     */
    const searchAffectedProducts = async () => {
        setLoading(true);
        const forApi = prepareSearchData();
        const response = await Api.searchAssignedPartsOfBulkPriceChange(forApi);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setProducts(response?.data?.items ?? []);
            setPaginationInfo(prevState => ({
                ...prevState,
                length: response.data?.totalCount ?? 0,
            }))
        }
    }

    /**
     * Prepares the search request body.
     */
    const prepareSearchData = () => {
        const res = {
            requestId: requestId,
            keyword: !keyword?.trim()?.length ? null : keyword.trim(),
            pagination: paginationInfo,
            sorts: [],
        };
        if (orderBy && orderByPropNames.includes(orderBy.field)) {
            res.sorts.push({
                property: orderBy.field,
                isDesc: orderBy.descending,
            });
        }
        return res;
    }

    /**
     * Memo version of table row records used in data grid
     */
    const dataGridRows = useMemo(() => {
        return products?.map((product) => ({
            key: product.partId,
            data: product,
            cells: {
                [BulkChangePriceAffectedProductsDataGridCellKeys.index]: product.partId,
                [BulkChangePriceAffectedProductsDataGridCellKeys.partNumber]:
                    <ProductInformationPopover partNo={product.partnumber}>
                        {product.partNumber ?? ''}
                    </ProductInformationPopover>,
                [BulkChangePriceAffectedProductsDataGridCellKeys.mainCategory]: product.categoryName,
                [BulkChangePriceAffectedProductsDataGridCellKeys.remove]:
                    <Button
                        style={{height: '30px'}}
                        variant={'outlined'}
                        color={'primary'}
                        onClick={() => toggleExcludedProduct(product.partId)}
                        disabled={disabled}
                    >
                        Remove
                    </Button>,

            }
        }))
    }, [products, disabled])

    return (
        <>
            <div>
                {
                    !hideTitle &&
                    <p className={'text-md font-weight-semi-bold mb-4'}>
                        Affected Products
                    </p>
                }
                <SearchInput
                    autoSearch
                    loading={loading}
                    initialValue={keyword ?? ""}
                    search={setKeyword}
                    label={'Search'}
                />
                <div className={'mb-2'}/>
                <div className={'content'}>
                    <PartnerDataGrid
                        hideFooterActions
                        hideExporting
                        {...(saveConfig ?? {})}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        columns={dataGridColumns}
                        rows={dataGridRows}
                        state={{
                            pagination: paginationInfo,
                            loading: {state: loading},
                            sortBy: orderBy,
                            density: dataGridDensity,
                        }}
                        onCurrentPageChanged={(_, current) => setPaginationInfo(current)}
                        onPageSizeChanged={(_, current) => setPaginationInfo(current)}
                        onSortByChanged={(_, current) => setOrderBy(current)}
                    />
                </div>
            </div>
        </>
    );
}

export default BulkPriceChangeAffectedProductsTable;
